/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module SnmpTrapListModule
 */

/* eslint-disable max-classes-per-file */

import {
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    Inject,
    NgModule,
    NO_ERRORS_SCHEMA,
    Type,
} from '@angular/core';

import { L10nService } from '@vmw/ngx-vip';
import { SNMP_TRAP_PROFILE_COLLECTION_TOKEN } from 'ajs/modules/alert/alert.tokens';

import {
    IAviCollectionDataGridConfig,
    IAviCollectionDataGridRow,
} from 'ng/modules/data-grid/components/avi-collection-data-grid/avi-collection-data-grid.types';

import { SnmpTrapProfileCollection }
    from 'ajs/modules/alert/factories/snmp-trap-profile/snmp-trap-profile.collection.factory';

import { AviDataGridFieldVisibility }
    from 'ng/modules/data-grid/components/avi-data-grid/avi-data-grid.types';

import { SnmpTrapServerConfigItem }
    from 'ajs/modules/alert/factories/snmp-trap-profile/snmp-trap-server.config-item.factory';

import { DataGridModule } from 'ng/modules/data-grid/data-grid.module';
import { SharedModule } from 'ng/shared/shared.module';
import { FullModalService } from 'ng/modules/core/services/full-modal/full-modal.service';

import {
    EmailModalTypeEnum,
    EmailValidationModalComponent,
} from 'ng/modules/email-validation-modal/email-validation-modal.component';

import * as globalL10n from 'global-l10n';
import * as l10n from './snmp-trap-list-page.l10n';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;

type TSnmpTrapProfileCollection = typeof SnmpTrapProfileCollection;

/**
 * @description SNMP Trap list page.
 * @author Manideep Kotha
 */
@Component({
    selector: 'snmp-trap-list-page',
    templateUrl: './snmp-trap-list-page.component.html',
})
export class SnmpTrapListPageComponent {
    public snmpTrapListGridConfig: IAviCollectionDataGridConfig;

    public readonly l10nKeys = l10nKeys;

    private readonly snmpTrapProfileCollection: SnmpTrapProfileCollection;

    private readonly globalL10nKeys = globalL10nKeys;

    constructor(
        private readonly l10nService: L10nService,
        @Inject(SNMP_TRAP_PROFILE_COLLECTION_TOKEN)
        SnmpTrapProfileCollection: TSnmpTrapProfileCollection,
        private readonly fullModalService: FullModalService,
    ) {
        l10nService.registerSourceBundles(dictionary);

        this.snmpTrapProfileCollection = new SnmpTrapProfileCollection();
    }

    /** @override */
    public ngOnInit(): void {
        const { objectName } = this.snmpTrapProfileCollection;

        this.snmpTrapListGridConfig = {
            id: `${objectName}-list-page`,
            collection: this.snmpTrapProfileCollection,
            fields: [],
            singleactions: [
                {
                    id: 'validation',
                    label: this.l10nService.getMessage(l10nKeys.validateSettingsLabel),
                    shape: 'host-group',
                    onClick: (row: IAviCollectionDataGridRow) => {
                        this.fullModalService.addModal({
                            component: EmailValidationModalComponent as Type<Component>,
                            componentProps: {
                                emailModalType: EmailModalTypeEnum.SNMP,
                                uuid: row.id,
                                onCancel: () => {
                                    this.fullModalService.removeModalByComponent(
                                        EmailValidationModalComponent as Type<Component>,
                                    );
                                },
                            },
                            getName: () => this.l10nService.getMessage(l10nKeys.snmpTitleLabel),
                            getDescription: () => '',
                        });
                    },
                },
            ],
            layout: {
                placeholderMessage: this.l10nService.getMessage(globalL10nKeys.noItemsFoundLabel),
            },
        };
    }

    /** @override */
    public ngAfterViewInit(): void {
        this.snmpTrapListGridConfig = {
            ...this.snmpTrapListGridConfig,
            fields: [
                {
                    id: 'name',
                    label: this.l10nService.getMessage(globalL10nKeys.nameLabel),
                    transform: (row: IAviCollectionDataGridRow) => row.getName(),
                    visibility: AviDataGridFieldVisibility.MANDATORY,
                },
                {
                    id: 'trap-servers',
                    label: this.l10nService.getMessage(globalL10nKeys.serverLabel),
                    transform: (row: IAviCollectionDataGridRow) => {
                        const output =
                            row.getConfig().trap_servers.config
                                .map((server: SnmpTrapServerConfigItem) =>
                                    server.config.ip_addr);

                        return output.join(', ');
                    },
                    visibility: AviDataGridFieldVisibility.MANDATORY,
                },
            ],
        };
    }

    public trackByIndex(index: number): number {
        return index;
    }
}

@NgModule({
    declarations: [
        SnmpTrapListPageComponent,
    ],
    imports: [
        DataGridModule,
        SharedModule,
    ],
    schemas: [
        CUSTOM_ELEMENTS_SCHEMA,
        NO_ERRORS_SCHEMA,
    ],
})
/* eslint-disable-next-line no-unused-vars */
class SnmpTrapListModule {}
